import { AnimatePresence, motion } from "motion/react"
import { memo } from "react"

interface PitchEventFlashProps {
  highlightType: "touchdown" | "turnover" | "kickoff" | null
  eventKey: number
}

const LAYER_STYLE: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
}

function TouchdownFlash() {
  return (
    <>
      {/* Vignette — gold inset shadow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 0.6, times: [0, 0.3, 1] }}
        style={{
          ...LAYER_STYLE,
          boxShadow: "inset 0 0 80px 20px oklch(0.78 0.18 55 / 0.3)",
        }}
      />

      {/* Radial burst — gold center */}
      <motion.div
        initial={{ opacity: 0, scale: 0.3 }}
        animate={{ opacity: [0, 0.7, 0], scale: [0.3, 1.2] }}
        transition={{ duration: 0.6, times: [0, 0.35, 1] }}
        style={{
          ...LAYER_STYLE,
          background:
            "radial-gradient(circle at 50% 50%, oklch(0.85 0.2 55 / 0.35) 0%, transparent 60%)",
        }}
      />

      {/* Text banner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1, 1, 1] }}
        transition={{ duration: 0.6, times: [0, 0.2, 0.6, 1] }}
        style={{
          ...LAYER_STYLE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-family-display)",
            fontSize: "clamp(2rem, 6vw, 4.5rem)",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "oklch(0.88 0.18 55)",
            textShadow:
              "0 0 20px oklch(0.78 0.18 55 / 0.8), 0 0 40px oklch(0.78 0.18 55 / 0.4), 0 2px 4px oklch(0 0 0 / 0.6)",
          }}
        >
          TOUCHDOWN!
        </span>
      </motion.div>
    </>
  )
}

function TurnoverFlash() {
  return (
    <>
      {/* Vignette — dark red inset shadow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 0.5, times: [0, 0.25, 1] }}
        style={{
          ...LAYER_STYLE,
          boxShadow: "inset 0 0 60px 15px oklch(0.55 0.18 28 / 0.35)",
        }}
      />

      {/* Red flash — full pitch tint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.5, 0] }}
        transition={{ duration: 0.3, times: [0, 0.3, 1] }}
        style={{
          ...LAYER_STYLE,
          background: "oklch(0.4 0.2 28 / 0.25)",
        }}
      />

      {/* Text banner — drifts down during fade */}
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: [0, 1, 1, 0], y: [0, 0, 0, 8] }}
        transition={{ duration: 0.5, times: [0, 0.15, 0.5, 1] }}
        style={{
          ...LAYER_STYLE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-family-display)",
            fontSize: "clamp(1.8rem, 5vw, 4rem)",
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: "oklch(0.65 0.22 28)",
            textShadow:
              "0 0 16px oklch(0.55 0.18 28 / 0.7), 0 0 32px oklch(0.5 0.18 28 / 0.3), 0 2px 4px oklch(0 0 0 / 0.6)",
          }}
        >
          TURNOVER
        </span>
      </motion.div>
    </>
  )
}

export const PitchEventFlash = memo(function PitchEventFlash({
  highlightType,
  eventKey,
}: PitchEventFlashProps) {
  const showEffect =
    highlightType === "touchdown" || highlightType === "turnover"

  return (
    <AnimatePresence>
      {showEffect && (
        <motion.div
          key={eventKey}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 30,
          }}
        >
          {highlightType === "touchdown" && <TouchdownFlash />}
          {highlightType === "turnover" && <TurnoverFlash />}
        </motion.div>
      )}
    </AnimatePresence>
  )
})
